import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Instrument } from "../../types/Types";
import { formatDMYHM } from "../../utils/DateUtils";
import AudioPlayer from "./AudioPlayer";

interface CardProps {
  id: string;
  title: string;
  description: string;
  instruments: Instrument[];
  audioSrc: string;
  isArchived: boolean;
  created_at: Date | null,
  onAudioPlay: (audio: HTMLAudioElement) => void;
  cardClassName: string;
  descClassName: string;
}

const Card: React.FC<CardProps> = ({
  id,
  title,
  description,
  instruments,
  audioSrc,
  isArchived,
  created_at,
  onAudioPlay,
  cardClassName,
  descClassName
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handleAudioPlay = () => {
    setIsPlaying(true);
    if (audioRef.current) {
      onAudioPlay(audioRef.current);
    }
  };

  // Callers can set their own line-clamp-N; otherwise default to 4 lines so
  // the waveform player still fits inside the fixed-height card.
  const descClamp = /\bline-clamp-\d+\b/.test(descClassName) ? "" : "line-clamp-4";

  return (
    // The player sits outside the link: as a descendant of the <a>, any
    // click on the waveform (or a seek-drag released over the text) would
    // also open the post. The link's ::after stretches over the whole card
    // instead, so everything except the player still navigates.
    <div
      className={`${cardClassName} relative flex flex-col bg-white rounded-3xl py-5 px-6 transition-shadow duration-300 ${
        isPlaying ? "shadow-lg ring-2 ring-primary/40" : "shadow-md hover:shadow-lg"
      }`}
    >
      <Link
        to={`/post-details/${id}`}
        className="block mb-2 after:absolute after:inset-0 after:rounded-3xl"
      >
        <div className="flex justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {isPlaying && (
                <span className="flex items-end gap-[2px] h-3.5 shrink-0" aria-hidden="true">
                  {[0, -0.3, -0.6].map((delay) => (
                    <span
                      key={delay}
                      className="w-[3px] h-[40%] rounded-[1px] bg-primary animate-equalizer"
                      style={{ animationDelay: `${delay}s` }}
                    />
                  ))}
                </span>
              )}
              <h2 className="text-xl font-semibold text-gray-800 line-clamp-1">{title}</h2>
            </div>
            <p className="text-xs text-gray-41"> Created at: {formatDMYHM(created_at)}</p>
          </div>
          {isArchived &&
            <span className="shrink-0 text-xs font-medium p-2 my-auto rounded bg-gray-200 uppercase tracking-wide">
              Archived
            </span>
          }
        </div>
        <p className={`text-gray-600 mb-4 mt-2 ${descClamp} ${descClassName}`}>{description}</p>
        {instruments && instruments.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {instruments.map((instrument) => (
              <span
                key={instrument.id}
                className="text-xs font-semibold text-primary bg-primary/[0.07] px-2.5 py-0.5 rounded-full"
              >
                {instrument.name}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-primary font-bold">-</p>
        )}
      </Link>
      <AudioPlayer
        className="w-full mt-auto relative z-10"
        ref={audioRef}
        waveformSeed={id}
        onPlay={handleAudioPlay}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      >
        <source src={audioSrc} type="audio/mpeg" />
        Your browser does not support the audio element.
      </AudioPlayer>
    </div>
  );
};

export default Card;
